// BodyAnimWizard — uploads a body-animation GLB.

import React, { useState } from 'react'
import WizardShell, { inputStyle, labelStyle, tokens } from './WizardShell'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { assetRegistry } from '../../../lib/assetRegistry'

function baseName(file) {
  return (file?.name ?? '').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_\-]+/g, '_')
}

export default function BodyAnimWizard({ file, path = [], onDone, onCancel }) {
  const [name, setName] = useState(baseName(file))
  // Not authored — see the note in GenerateBodyAnimWizard. 'other' is the
  // server default for animation_assets.category; nothing branches on it.
  const category = 'other'
  const [additive, setAdditive] = useState(false)
  const [loopable, setLoopable] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    if (!name.trim()) { setError('Name is required'); return }
    setSubmitting(true); setError(null)
    try {
      const row = await CustomAssetManager.uploadAnimationAsset(file, {
        name: name.trim(),
        kind: 'body',
        category,
        additive,
        loopable,
      })
      assetRegistry.setPath(`anim:${row.id}`, path)
      onDone?.(row)
    } catch (e) {
      setError(String(e.message ?? e))
      setSubmitting(false)
    }
  }

  return (
    <WizardShell
      title="New body animation"
      subtitle={file?.name}
      onCancel={onCancel}
      onSubmit={submit}
      submitDisabled={submitting || !name.trim()}
      submitLabel={submitting ? 'Uploading…' : 'Upload'}
      error={error}
    >
      <label style={labelStyle}>Name</label>
      <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} autoFocus />

      <label style={labelStyle}>Flags</label>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Check label="Additive (layered gesture)" checked={additive} onChange={setAdditive} />
        <Check label="Loopable" checked={loopable} onChange={setLoopable} />
      </div>

      <div style={{ marginTop: 14, fontSize: 10, fontFamily: 'var(--m-font)', color: tokens.dim }}>
        Body clips upload to <code>/animation-assets</code> and register at
        runtime as <code>custom_body_&lt;id&gt;</code>. Additive clips are
        baked deltas at load time (see <code>AnimationUtils.makeClipAdditive</code>).
      </div>
    </WizardShell>
  )
}

function Check({ label, checked, onChange }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: tokens.text, fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  )
}
